import { FC, ReactNode, useCallback, useMemo } from "react";

import { DonationFormCategory, FormType } from "@simplyk/common";

import { useLiveFormAmplitudeEvents } from "./common/amplitude/useLiveFormAmplitudeEvents";
import { LiveFormActionFooterRef } from "./common/LiveFormEditorActionFooter";
import { useLiveFormEditorContext } from "./common/LiveFormEditorContext";
import { SaveFormModal } from "./common/SaveFormModal";
import { LiveFormTopBar } from "./Navigation/LiveFormTopBar";
import { FORM_STEP_TO_ELEMENT_ID } from "./Steps/helpers/formStepToElementIdMap";
import { getFormStepSidebarItems } from "./Steps/helpers/getFormStepSidebarItems";
import { LiveEditorStepKey } from "./Steps/LiveEditorStep.enum";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ArrowProvider } from "@/components/LiveEditorElements/AnimatePreview/Arrow/ArrowContext";
import { LiveEditorLayout } from "@/components/LiveEditorElements/LiveEditorLayout/LiveEditorLayout";
import { JumpToSideBar, NavigationItem } from "@/components/LiveEditorElements/Navigation/JumpToSideBar/JumpToSideBar";
import { ConnectedShareFormModal } from "@/components/ShareFormModal";

type LiveFormEditorBaseProps = {
  slots: {
    steps: ReactNode;
    preview: ReactNode;
  };
};

export const LiveFormEditorBase: FC<LiveFormEditorBaseProps> = ({ slots }) => {
  const { t } = useTranslate();
  const {
    currentStepKey,
    editOrCreateMode: mode,
    shareFormModalState,
    isStepChanging,
    apiFormObject,
    formType,
    isMobilePreviewOpen,
    setIsMobilePreviewOpen,
    currentStepIndex,
    totalNumberOfSteps,
    isNavigationOpen,
    setIsNavigationOpen,
    liveEditorStepsKeys,
    updateCurrentStep,
    performActionWithSaveGuard,
    formCategory,
    remindersData,
    showRenewalEmails,
    iframeRef,
    previewScale,
    editorMode,
  } = useLiveFormEditorContext();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const isPeerToPeerCampaignEditedByOrg =
    formCategory === DonationFormCategory.PeerToPeerCampaign && editorMode === "org";
  const hasAutomatedEmails = remindersData.showReminders || showRenewalEmails || isPeerToPeerCampaignEditedByOrg;
  const stepSidebarItems = useMemo(
    () => getFormStepSidebarItems({ formCategory, hasAutomatedEmails }),
    [formCategory, hasAutomatedEmails]
  );

  const navigationItems = useMemo((): NavigationItem[] => {
    return liveEditorStepsKeys.map((stepKey) => ({
      id: stepKey,
      label: t("dashboard", stepSidebarItems[stepKey]),
      isActive: currentStepKey === stepKey,
    }));
  }, [liveEditorStepsKeys, stepSidebarItems, currentStepKey, t]);

  const handleNavigationItemClick = useCallback(
    (itemId: string, onNavigationComplete: () => void) => {
      logUserActionEvent(AmplitudeEvents.FormNavStepClicked, {
        step_selected: itemId.toLowerCase(),
      });

      // Lock the sidebar open via prop to prevent mouseLeave from closing it
      // when the save modal appears (in case sidebar was opened by hover)
      setIsNavigationOpen(true);

      performActionWithSaveGuard({
        action: async () => {
          updateCurrentStep(itemId as LiveEditorStepKey);
          onNavigationComplete();
        },
      });
    },
    [logUserActionEvent, performActionWithSaveGuard, setIsNavigationOpen, updateCurrentStep]
  );

  const ratio = currentStepKey === "review" ? "5-7" : "6-6";

  return (
    <ArrowProvider
      currentStepKey={currentStepKey}
      iframeRef={iframeRef}
      previewScale={previewScale}
      stepToElementIdMap={FORM_STEP_TO_ELEMENT_ID}
    >
      <LiveEditorLayout
        slots={{
          topBar: <LiveFormTopBar />,
          sideBar:
            mode === "edit" ? (
              <JumpToSideBar
                isOpen={isNavigationOpen}
                onClose={() => setIsNavigationOpen(false)}
                items={navigationItems}
                onItemClick={handleNavigationItemClick}
              />
            ) : undefined,
          steps: slots.steps,
          preview: slots.preview,
          actionFooter: <LiveFormActionFooterRef />,
        }}
        ratio={ratio}
        stepsKey={`${currentStepKey}-${isStepChanging}`}
        mobilePreview={{ isOpen: isMobilePreviewOpen, onClose: () => setIsMobilePreviewOpen(false) }}
        mobileProgressBar={{
          show: mode === "create",
          currentStepIndex,
          totalNumberOfSteps,
        }}
      />

      {/* Modals */}
      <SaveFormModal />
      <ConnectedShareFormModal
        key={apiFormObject.path}
        formId={apiFormObject.id}
        formType={formType === "donation-form" ? FormType.DonationForm : FormType.Ticketing}
        open={shareFormModalState.isOpen}
        onClose={shareFormModalState.closeModal}
        source="liveFormEditor"
      />
    </ArrowProvider>
  );
};
